import { Button, Form, Input, InputNumber, message, Modal, Popover, Select, Tabs } from 'antd';
import TabPane from 'antd/es/tabs/TabPane';
import React from 'react';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { REQUIRED_FIELD } from 'constants/common';
import FormValidationHelper from 'utils/validation';
import useInsuranceForecast from 'hooks/useInsuranceForecast';
import { TInsuranceForecast, TInsuranceForecastCalculate } from 'types/common';
import { calculateInsuranceForecast, saveInsuranceForecastList } from 'api/common';
import { getUserId } from 'utils/localstore';
import dayjs from 'dayjs';

const { Option } = Select;

type Props = {
    open: boolean,
    onCancel: () => void,
    onSuccess: () => void,
};

const ReimbursementsSetupModal = (props: Props) => {
    const { open, onCancel, onSuccess } = props;
    const [form] = Form.useForm<TInsuranceForecast>();
    const [insuranceForecast, loadingInsuranceForecast, fetchInsuranceForecast] = useInsuranceForecast();
    const [calculateResult, setCalculateResult] = React.useState<TInsuranceForecastCalculate>();
    const currentInsuranceForecast = insuranceForecast?.length > 0 ? insuranceForecast[0] : undefined;
    const [submitting, setSubmitting] = React.useState(false);
    const [earnLabel, setEarnLabel] = React.useState('');
    const [earnDate, setEarnDate] = React.useState('');
    const [calculateLoading, setCalculateLoading] = React.useState(false);
    const showInfo = earnLabel && earnDate && !calculateLoading;
    const [forceCalculateFlag, forceCalculate] = React.useState<{}>();
    const initAllFormValues = {
        year: 10,
        rate: 10,
        reimbursement: 100,
        cashPay: 100,
        delay: 10,
        monthlyInsuranceEarnings: 100,
    };

    const [formValues, setFormValues] = React.useState<TInsuranceForecast>();

    React.useEffect(() => {
        if (currentInsuranceForecast) {
            form.setFieldsValue(currentInsuranceForecast);
            setEarnLabel(`$${currentInsuranceForecast.estimatedMonthlyInsuranceIncome}`);
            if (currentInsuranceForecast.forecastStartMonth) {
                setEarnDate(dayjs(currentInsuranceForecast.forecastStartMonth).format('MMMM YYYY'));
            }
            setFormValues(currentInsuranceForecast);
        }
    }, [currentInsuranceForecast, form]);

    React.useEffect(() => {
        const doCalculate = async () => {
            try {
                setCalculateLoading(true);
                const res = await calculateInsuranceForecast(formValues!);
                if (!res?.error) {
                    const data = res?.data.data;
                    setCalculateResult(data);
                    form.setFieldsValue({
                        estimatedMonthlyTotalIncome: data.estimatedMonthlyTotalIncome || 0,
                    });
                } else {
                    setCalculateResult(undefined);
                    setEarnDate('');
                    setEarnLabel('');
                }
            } catch (error) {
                console.error(error);
            } finally {
                setCalculateLoading(false);
            }
        };
        if (formValues) {
            doCalculate();
        }
    }, [form, formValues]);

    React.useEffect(() => {
        if (calculateResult) {
            setEarnLabel(`$${calculateResult.estimatedMonthlyInsuranceIncome}`);
            setEarnDate(dayjs(calculateResult.forecastStartMonth).format('MMMM YYYY'));
        }
    }, [calculateResult]);

    const handleOk = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();
            const resData: TInsuranceForecast = {
                ...(currentInsuranceForecast || {}),
                ...(calculateResult || {}),
                ...values,
                providerId: getUserId(),
            };
            // if (currentInsuranceForecast) {
            //     resData.providerId = currentInsuranceForecast.providerId;
            //     resData.id = currentInsuranceForecast.id;
            // }
            if (calculateResult) {
                resData.forecastStartMonth = calculateResult.forecastStartMonth;
            }
            const res = await saveInsuranceForecastList(resData);
            if (!res?.error) {
                message.success('Insurance forecast has been updated');
                fetchInsuranceForecast();
                onSuccess();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className={s.modal}
        >
            <Modal
                open={open}
                onCancel={onCancel}
                okText="Save and update forecast"
                title="Estimate your insurance reimbursements"
                className={commonS.modalFixHeightWrap}
                onOk={handleOk}
                okButtonProps={{
                    loading: submitting,
                }}
            >
                <div className={s.mWrap}>
                    <div className={s.tips}>
                        Enter your insurance reimbursement to estimate your potential income. The forecast is assumption-based and updates automatically as you adjust your inputs.
                    </div>
                    <Form
                        initialValues={initAllFormValues}
                        form={form}
                        layout="vertical"
                        onValuesChange={(change, allValues) => {
                            setFormValues(allValues);
                        }}
                    >
                        <div className={s.inputList}>
                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Initial visit reimbursement ($)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        Average insurance payment you receive for a new patient’s first visit, not including any copay from the patient.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="initialVisitReimbursement"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" prefix="$" className={s.input} />
                                </Form.Item>
                            </div>
                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Follow-up visit reimbursement ($)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        Average insurance payment for each follow-up visit, excluding the patient’s copay portion.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="followupVisitReimbursement"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" prefix="$" className={s.input} />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Average copay ($)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        The average out-of-pocket copay patients pay for each insured visit.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="averageCopay"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" prefix="$" className={s.input} />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Copay collection rate (%)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        The percentage of copays that are successfully collected from patients during or after the visit.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="copayCollectionRatePct"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" type="number" className={s.input} suffix="%" />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Insurance payment delay (days)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        The average number of days insurance companies take to process and pay your claims.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="insurancePaymentDelayDays"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" type="number" className={s.input} />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Average follow-up visits per patient per year
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        Estimated number of follow-up visits each patient completes per year.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="followupVisitsPerPatientPerYear"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" type="number" className={s.input} />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Appointment attendance rate (%)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        The percentage of scheduled appointments that are actually completed by patients. Applies to both initial and follow-up visits.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="appointmentAttendanceRatePct"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" type="number" className={s.input} suffix="%" />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Average cash-pay per visit ($)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        Typical self-pay visit price charged to patients who pay out of pocket (not insurance).
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="averageCashPayPerVisit"
                                    className={s.formItem}
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateNumber('Please enter a valid number'),
                                    ]}
                                >
                                    <Input size="large" prefix="$" className={s.input} />
                                </Form.Item>
                            </div>

                            <div className={s.inputItem}>
                                <Form.Item
                                    label={
                                        <div className={s.label}>
                                            Estimated monthly total earnings ($)
                                            <Popover
                                                trigger="hover"
                                                content={(
                                                    <div className={s.pop}>
                                                        Your combined monthly income estimate. Auto-calculates and updates as you edit your inputs.
                                                    </div>
                                                )}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                    <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </Popover>
                                        </div>}
                                    name="estimatedMonthlyTotalIncome"
                                    className={s.formItem}
                                >
                                    <Input disabled size="large" prefix="$" className={s.input} />
                                </Form.Item>
                            </div>
                        </div>
                    </Form>
                    {
                        showInfo && (
                            <div className={s.info}>
                                <div className={s.left}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 13.6 2.376 15.112 3.043 16.453C3.221 16.809 3.28 17.216 3.177 17.601L2.582 19.827C2.52307 20.0473 2.52312 20.2792 2.58216 20.4995C2.64119 20.7197 2.75712 20.9206 2.91831 21.0819C3.07951 21.2432 3.2803 21.3593 3.50053 21.4184C3.72075 21.4776 3.95267 21.4778 4.173 21.419L6.399 20.823C6.78538 20.7254 7.19414 20.7727 7.548 20.956C8.93095 21.6446 10.4551 22.002 12 22Z" stroke="#0E62F4" strokeWidth="1.5" />
                                        <path d="M12 15.333C13.105 15.333 14 14.587 14 13.667C14 12.747 13.105 12 12 12C10.895 12 10 11.254 10 10.333C10 9.413 10.895 8.667 12 8.667M12 15.333C10.895 15.333 10 14.587 10 13.667M12 15.333V16M12 8.667V8M12 8.667C13.105 8.667 14 9.413 14 10.333" stroke="#0E62F4" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div className={s.right}>
                                    <div className={s.tips}>
                                        Projected earnings update
                                    </div>
                                    <div className={s.content}>
                                        Based on your inputs and payment delay, your estimated monthly insurance income could be around <b>{earnLabel}</b>, beginning in <b>{earnDate}</b>.
                                    </div>
                                    <div className={s.note}>
                                        This forecast is for reference only and will adjust as your assumptions change.
                                    </div>

                                </div>
                            </div>
                        )
                    }
                </div>
            </Modal>
        </div>
    );
};

export default ReimbursementsSetupModal;
